import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { AssigneeStack, AssigneePicker } from "@/components/app/AssigneePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Search, SlidersHorizontal, Save, Loader2, Calendar, MessageSquare, Send, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";

const COLUMNS = [
  { key: "todo",        title: "To do",      accent: "#374151" },
  { key: "in_progress", title: "In progress", accent: "#002FA7" },
  { key: "review",      title: "Review",      accent: "#F59E0B" },
  { key: "done",        title: "Done",        accent: "#00F5D4" },
];

const PRIORITY_COLORS = {
  low:      "bg-slate-200 text-slate-700",
  medium:   "bg-orange-400 text-gray-900",
  high:     "bg-amber-600 text-white",
  critical: "bg-red-600 text-white",
};

const ITEM_TYPES = [
  { key: "task",      label: "Task" },
  { key: "story",     label: "Story" },
  { key: "bug",       label: "Bug" },
  { key: "epic",      label: "Epic" },
  { key: "milestone", label: "Milestone" },
  { key: "phase",     label: "Phase" },
];

const ITEM_TYPE_STYLES = {
  task:      "bg-[#111827] text-slate-300",
  story:     "bg-emerald-100 text-emerald-800",
  bug:       "bg-red-100 text-red-700",
  epic:      "bg-violet-100 text-violet-700",
  milestone: "bg-amber-100 text-amber-700",
  phase:     "bg-blue-900 text-[#FFFFFF]",
};

const emptyForm = {
  title: "", description: "", status: "todo", priority: "medium",
  assignees: [], item_type: "task", story_points: "", estimated_hours: "",
  due_date: "",
};

// ─── Task detail side-drawer ─────────────────────────────────────────────────
const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
};

const TaskDrawer = ({ task, members, onClose, onSaved, onDeleted }) => {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState("");
  const [posting, setPosting] = useState(false);

  const loadComments = useCallback(async (taskId) => {
    try {
      const { data } = await api.get(`/tasks/${taskId}/comments`);
      setComments(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (task) {
      setForm({
        title:           task.title || "",
        description:     task.description || "",
        status:          task.status || "todo",
        priority:        task.priority || "medium",
        assignees:       task.assignees || [],
        item_type:       task.item_type || "task",
        story_points:    task.story_points != null ? String(task.story_points) : "",
        estimated_hours: task.estimated_hours != null ? String(task.estimated_hours) : "",
        due_date:        task.due_date ? task.due_date.slice(0, 10) : "",
      });
      setComments([]);
      setCommentBody("");
      loadComments(task.task_id);
    }
  }, [task, loadComments]);

  const postComment = async () => {
    const body = commentBody.trim();
    if (!body || posting) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/tasks/${task.task_id}/comments`, { body });
      setComments((prev) => [...prev, data]);
      setCommentBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (commentId) => {
    setComments((prev) => prev.filter((c) => c.comment_id !== commentId));
    try { await api.delete(`/tasks/${task.task_id}/comments/${commentId}`); }
    catch { loadComments(task.task_id); }
  };

  if (!task || !form) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title:           form.title.trim(),
        description:     form.description,
        status:          form.status,
        priority:        form.priority,
        assignees:       form.assignees,
        item_type:       form.item_type,
        story_points:    form.story_points !== "" ? parseInt(form.story_points, 10) : null,
        estimated_hours: form.estimated_hours !== "" ? parseFloat(form.estimated_hours) : null,
        due_date:        form.due_date || null,
      };
      const { data } = await api.patch(`/tasks/${task.task_id}`, payload);
      toast.success("Task saved");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this task?")) return;
    setDeleting(true);
    try {
      await api.delete(`/tasks/${task.task_id}`);
      toast.success("Task deleted");
      onDeleted(task.task_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const FIELD = "mt-1 w-full rounded-md bg-[#030712] border border-[#1F2937] text-slate-100 px-3 py-2 text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#00F5D4]/40";
  const LBL  = "text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono";

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="task-drawer-overlay"
      />
      {/* Panel */}
      <div
        className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-[#0D1117] border-l border-[#1F2937] shadow-2xl flex flex-col"
        data-testid="task-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1F2937]">
          <div className="text-xs uppercase tracking-[0.25em] text-slate-400 font-mono">Task detail</div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition" data-testid="close-task-drawer">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {/* Title */}
          <div>
            <label className={LBL}>Title</label>
            <input
              className={FIELD}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="drawer-title"
            />
          </div>
          {/* Description */}
          <div>
            <label className={LBL}>Description</label>
            <textarea
              className={`${FIELD} resize-none min-h-[80px]`}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="drawer-description"
            />
          </div>
          {/* Row: type + status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LBL}>Type</label>
              <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                <SelectTrigger className="mt-1 rounded-md bg-[#030712] border-[#1F2937] text-white text-sm h-9" data-testid="drawer-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1F2937]">
                  {ITEM_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={LBL}>Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 rounded-md bg-[#030712] border-[#1F2937] text-white text-sm h-9" data-testid="drawer-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1F2937]">
                  {COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Row: priority + due date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LBL}>Priority</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1 rounded-md bg-[#030712] border-[#1F2937] text-white text-sm h-9" data-testid="drawer-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1F2937]">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={LBL}>Due date</label>
              <input
                type="date"
                className={`${FIELD} h-9`}
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="drawer-due-date"
              />
            </div>
          </div>
          {/* Assignees */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={LBL}>Assignees</label>
              {user && !form.assignees?.includes(user.user_id) && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, assignees: [...(form.assignees || []), user.user_id] })}
                  className="text-[10px] text-[#00F5D4] hover:text-white uppercase tracking-[0.15em] transition"
                >
                  + Assign to me
                </button>
              )}
            </div>
            <div className="mt-1">
              <AssigneePicker
                value={form.assignees}
                members={members}
                onChange={(ids) => setForm({ ...form, assignees: ids })}
                testId="drawer-assignees"
              />
            </div>
          </div>
          {/* Story points + hours */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LBL}>Story points</label>
              <input
                type="number"
                min="0"
                className={FIELD}
                placeholder="e.g. 5"
                value={form.story_points}
                onChange={(e) => setForm({ ...form, story_points: e.target.value })}
                data-testid="drawer-story-points"
              />
            </div>
            <div>
              <label className={LBL}>Est. hours</label>
              <input
                type="number"
                min="0"
                step="0.5"
                className={FIELD}
                placeholder="e.g. 8.5"
                value={form.estimated_hours}
                onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })}
                data-testid="drawer-est-hours"
              />
            </div>
          </div>

          {/* Comments */}
          <div className="border-t border-[#1F2937] pt-4">
            <div className="flex items-center gap-1.5 mb-3">
              <MessageSquare className="w-3.5 h-3.5 text-slate-500" />
              <span className={LBL}>Comments</span>
              {comments.length > 0 && <span className="text-[10px] text-slate-600 font-mono">({comments.length})</span>}
            </div>
            {comments.length > 0 && (
              <div className="space-y-3 mb-3" data-testid="comments-list">
                {comments.map((c) => (
                  <div key={c.comment_id} className="group flex gap-2.5" data-testid={`comment-${c.comment_id}`}>
                    <div className="w-6 h-6 rounded-full bg-[#1F2937] flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-slate-400 uppercase">
                      {(c.author_name || "?")[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold text-slate-300">{c.author_name}</span>
                        <span className="text-[10px] text-slate-600 font-mono">{fmtTime(c.created_at)}</span>
                        {c.author_id === user?.user_id && (
                          <button
                            onClick={() => deleteComment(c.comment_id)}
                            className="opacity-0 group-hover:opacity-100 ml-auto text-slate-600 hover:text-[#FF007A] transition"
                            data-testid={`delete-comment-${c.comment_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5 whitespace-pre-wrap">{c.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                className={`${FIELD} resize-none min-h-[56px] flex-1 text-xs`}
                placeholder="Add a comment…"
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); postComment(); } }}
                data-testid="comment-input"
              />
              <button
                onClick={postComment}
                disabled={posting || !commentBody.trim()}
                className="px-3 py-2 bg-[#002FA7]/20 hover:bg-[#002FA7]/40 border border-[#002FA7]/30 text-[#002FA7] rounded-md transition disabled:opacity-40 flex-shrink-0"
                data-testid="post-comment-btn"
              >
                {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-t border-[#1F2937] px-5 py-4 flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving || !form.title.trim()}
            className="flex-1 flex items-center justify-center gap-2 bg-[#00F5D4]/10 hover:bg-[#00F5D4]/20 border border-[#00F5D4]/30 text-[#00F5D4] text-sm font-mono py-2 rounded-md transition disabled:opacity-50"
            data-testid="drawer-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
          <button
            onClick={del}
            disabled={deleting}
            className="px-4 py-2 text-sm text-[#FF007A]/70 hover:text-[#FF007A] border border-[#FF007A]/20 hover:border-[#FF007A]/40 rounded-md transition"
            data-testid="drawer-delete"
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Delete"}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Main board ──────────────────────────────────────────────────────────────
const AgileBoard = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [draggedId, setDraggedId] = useState(null);
  const [dragTarget, setDragTarget] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(async () => {
    const [p, t, m] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/tasks?project_id=${projectId}`),
      api.get(`/team/members`).catch(() => ({ data: [] })),
    ]);
    setProject(p.data);
    setTasks(t.data);
    setMembers(m.data);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    let t = tasks;
    if (search.trim()) {
      const q = search.toLowerCase();
      t = t.filter((x) => x.title?.toLowerCase().includes(q));
    }
    if (typeFilter !== "all") t = t.filter((x) => x.item_type === typeFilter);
    return t;
  }, [tasks, search, typeFilter]);

  const create = async () => {
    if (!form.title.trim()) return;
    const payload = {
      project_id: projectId, title: form.title, description: form.description,
      status: form.status, priority: form.priority, assignees: form.assignees,
      item_type: form.item_type,
      story_points:    form.story_points    ? parseInt(form.story_points, 10)  : null,
      estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : null,
      due_date: form.due_date || null,
    };
    try {
      const { data } = await api.post("/tasks", payload);
      setTasks((prev) => [...prev, data]);
      setForm(emptyForm);
      setOpen(false);
      toast.success("Task added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add task");
    }
  };

  const moveTask = async (taskId, newStatus) => {
    setTasks((prev) => prev.map((t) => (t.task_id === taskId ? { ...t, status: newStatus } : t)));
    try {
      await api.patch(`/tasks/${taskId}/status`, { status: newStatus });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to move task");
      load();
    }
  };

  const onTaskSaved = (updated) => {
    setTasks((prev) => prev.map((t) => (t.task_id === updated.task_id ? { ...t, ...updated } : t)));
    setSelectedTask(null);
  };

  const onTaskDeleted = (id) => {
    setTasks((prev) => prev.filter((t) => t.task_id !== id));
    setSelectedTask(null);
  };

  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
    catch { return iso; }
  };

  return (
    <DarkPage>
      <PageHeader
        backTo={`/workspace/${projectId}`}
        eyebrow={project ? `Board · ${project.name}` : "Board"}
        title="Agile Board"
        description="Drag tasks to update status. Click a card to edit. Built for Agile, Waterfall and Hybrid."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Link to={`/workspace/${projectId}`}>
              <Button variant="outline" className="rounded-none border-[#374151] text-[#9CA3AF] hover:text-white bg-transparent" data-testid="back-to-project-btn">← Project</Button>
            </Link>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="new-task-btn">
                  <Plus className="w-4 h-4 mr-1" /> New task
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-md">
                <DialogHeader><DialogTitle className="font-display tracking-tight">New task</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Title</Label>
                    <Input className="rounded-none mt-1" data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Description</Label>
                    <Textarea className="rounded-none mt-1" data-testid="task-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Type</Label>
                      <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="task-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ITEM_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Priority</Label>
                      <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Due date</Label>
                      <Input type="date" className="rounded-none mt-1" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-[11px] uppercase tracking-[0.2em]">Assignees</Label>
                        {user && !form.assignees?.includes(user.user_id) && (
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, assignees: [...(form.assignees || []), user.user_id] })}
                            className="text-[10px] text-[#00F5D4] hover:text-white uppercase tracking-[0.15em] transition"
                          >
                            + Assign to me
                          </button>
                        )}
                      </div>
                      <AssigneePicker value={form.assignees} members={members} onChange={(ids) => setForm({ ...form, assignees: ids })} testId="task-assignee-picker" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Story points</Label>
                      <Input type="number" min="0" className="rounded-none mt-1" placeholder="e.g. 5" data-testid="task-story-points-input" value={form.story_points} onChange={(e) => setForm({ ...form, story_points: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Est. hours</Label>
                      <Input type="number" min="0" step="0.5" className="rounded-none mt-1" placeholder="e.g. 8.5" data-testid="task-est-hours-input" value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={create} className="rounded-none bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="confirm-create-task">Add task</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {/* Filter bar */}
      <div className="px-5 sm:px-8 lg:px-12 pt-4 pb-2 border-b border-[#1F2937] flex flex-wrap gap-3 items-center bg-[#030712]">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6B7280]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks…"
            className="pl-9 pr-3 h-8 w-full bg-[#111827] border border-[#1F2937] text-sm text-white placeholder:text-[#4B5563] rounded-md focus:outline-none focus:border-[#00F5D4]/40"
            data-testid="board-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-32 h-8 bg-[#111827] border-[#1F2937] text-sm text-white rounded-md" data-testid="board-type-filter">
            <SlidersHorizontal className="w-3 h-3 mr-1.5 text-[#6B7280]" />
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937]">
            <SelectItem value="all">All types</SelectItem>
            {ITEM_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || typeFilter !== "all") && (
          <button onClick={() => { setSearch(""); setTypeFilter("all"); }} className="text-xs text-[#6B7280] hover:text-white transition" data-testid="clear-board-filters">
            Clear · {visible.length}
          </button>
        )}
      </div>

      {/* Board columns */}
      <div className="px-5 sm:px-8 lg:px-12 py-6">
        <div
          className="grid gap-px bg-[#1F2937] border border-[#1F2937]"
          style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(0, 1fr))` }}
        >
          {COLUMNS.map((col) => {
            const colTasks = visible.filter((t) => t.status === col.key);
            const isDrop = dragTarget === col.key;
            return (
              <div
                key={col.key}
                className={`bg-[#030712] kanban-col transition-colors ${isDrop ? "bg-[#111827]" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragTarget(col.key); }}
                onDragLeave={() => setDragTarget(null)}
                onDrop={() => {
                  if (draggedId) { moveTask(draggedId, col.key); setDraggedId(null); }
                  setDragTarget(null);
                }}
                data-testid={`column-${col.key}`}
              >
                <div className="px-4 py-3 border-b border-[#1F2937] flex items-center justify-between bg-[#0D1117]">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: col.accent }} />
                    <span className="text-xs uppercase tracking-[0.25em] text-slate-300">{col.title}</span>
                  </div>
                  <span className="text-xs font-mono text-gray-500" data-testid={`col-count-${col.key}`}>{colTasks.length}</span>
                </div>
                <div className="p-3 space-y-2 min-h-[300px]">
                  {colTasks.map((t) => {
                    const isPhase = t.item_type === "phase";
                    const isMilestone = t.item_type === "milestone";
                    const typeStyle = ITEM_TYPE_STYLES[t.item_type] || ITEM_TYPE_STYLES.task;
                    return (
                      <div
                        key={t.task_id}
                        draggable
                        onDragStart={() => setDraggedId(t.task_id)}
                        onClick={() => setSelectedTask(t)}
                        className={`border p-3 cursor-pointer hover:border-[#374151] group transition-colors ${
                          isPhase ? "bg-[#0D1117] text-white border-[#374151]"
                          : isMilestone ? "bg-amber-900/20 border-amber-700/50"
                          : "bg-[#0D1117] border-[#1F2937]"
                        }`}
                        data-testid={`task-card-${t.task_id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <span className={`text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 flex-shrink-0 ${isPhase ? "bg-blue-900 text-[#FFFFFF]" : typeStyle}`}>{t.item_type || "task"}</span>
                            <div className="font-medium text-sm truncate">{t.title}</div>
                          </div>
                        </div>
                        {t.description && <div className={`text-xs mt-1 line-clamp-2 ${isPhase ? "text-gray-300" : "text-slate-400"}`}>{t.description}</div>}
                        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {t.priority && <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${isPhase ? "bg-gray-200 text-gray-700" : PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>}
                            {t.story_points > 0 && (
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 ${isPhase ? "bg-gray-200 text-gray-700" : "bg-emerald-100 text-emerald-800"}`} data-testid={`story-points-${t.task_id}`}>{t.story_points} SP</span>
                            )}
                            {t.estimated_hours != null && (
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 ${isPhase ? "bg-gray-200 text-gray-700" : "bg-blue-100 text-blue-700"}`} data-testid={`est-hours-${t.task_id}`}>{t.estimated_hours}h</span>
                            )}
                          </div>
                          <div className={`flex items-center gap-2 text-[10px] ${isPhase ? "text-gray-300" : "text-gray-500"}`}>
                            {t.due_date && (
                              <span className="flex items-center gap-1 font-mono">
                                <Calendar className="w-2.5 h-2.5" />
                                {fmtDate(t.due_date)}
                              </span>
                            )}
                            <AssigneeStack assigneeIds={t.assignees} members={members} size="w-5 h-5" max={3} testId={`task-assignees-${t.task_id}`} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Task detail drawer */}
      {selectedTask && (
        <TaskDrawer
          task={selectedTask}
          members={members}
          onClose={() => setSelectedTask(null)}
          onSaved={onTaskSaved}
          onDeleted={onTaskDeleted}
        />
      )}
    </DarkPage>
  );
};

export default AgileBoard;
